import { createSelector } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import { InsightsState, InsightsStateSelector } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { DiseaseEventListGlobalViewSortOptions, DiseaseEventListLocationViewSortOptions } from 'models/SortByOptions';
import { containsNoCaseNoLocale } from 'utils/stringHelpers';

import { GetSelectedRisk } from 'components/RisksProjectionCard/RisksProjectionCard';

import diseaseActivitiesGlobalSelectors from 'diseaseActivitiesGlobal/diseaseActivitiesGlobal.selectors';
import diseaseActivitiesInLocationSelectors from 'diseaseActivitiesInLocation/diseaseActivitiesInLocation.selectors';
import { mapDiseaseActivityParamsToPanelState } from 'diseaseActivityPanels/disacPanelUtils';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';

const diseaseActivityList = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.disacListData;
const isLoading = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.isLoading;
const isLoadingWrtPreviousPanel = createSelector(
  isLoading,
  diseaseActivitiesInLocationSelectors.isLoading,
  diseaseActivitiesGlobalSelectors.isLoading,
  diseaseActivityPanelsSelectors.isGlobalSelected,
  (isLoadingDELP, isLoadingDLP, isLoadingDLPGlobal, isGlobal) => isLoadingDELP || (isGlobal ? isLoadingDLPGlobal : isLoadingDLP)
);
const hasError = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.hasError;
const eventTitle = createSelector(diseaseActivityList, x => x?.diseaseInformation?.name);
const diseaseActivitiesByCountryId = createSelector(diseaseActivityList, x => x?.eventsList);
const importationRisk = createSelector(diseaseActivityList, x => x?.importationRisk);
const exportationRisk = createSelector(diseaseActivityList, x => x?.exportationRisk);
const outbreakPotentialCategory = createSelector(diseaseActivityList, x => x?.outbreakPotentialCategory);
const diseaseInformation = createSelector(diseaseActivityList, x => x?.diseaseInformation);
const selectedRiskType = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.selectedListRiskType;
const selectedRisk = createSelector(diseaseActivityList, selectedRiskType, GetSelectedRisk);
const getDiseaseActivityByCountrySortBy = (state: InsightsState) => state.diseaseActivitiesInCountryLocations.disacListActivityByCountrySortBy;
const getDiseaseActivityByCountrySearchString = (state: InsightsState) =>
  state.diseaseActivitiesInCountryLocations.disacListActivityByCountrySearchString;
const eventListSortOptions = createSelector(diseaseActivityPanelsSelectors.isGlobalSelected, isGlobalSelected =>
  isGlobalSelected ? DiseaseEventListGlobalViewSortOptions : DiseaseEventListLocationViewSortOptions
);
const getFilteredEventList: InsightsStateSelector<dto.GetEventModel[]> = createSelector(
  diseaseActivityList,
  getDiseaseActivityByCountrySortBy,
  getDiseaseActivityByCountrySearchString,
  eventListSortOptions,
  (disacListData, sortBy, searchString, sortOptions) =>
    disacListData
      ? orderBy(
          disacListData.eventsList,
          sortOptions.find(so => so.value === sortBy).keys,
          sortOptions.find(so => so.value === sortBy).orders
        ).filter(o => containsNoCaseNoLocale(o.eventInformation.title, searchString))
      : []
);
const getTotalCaseCounts = createSelector(
  diseaseActivityList,
  x =>
    x?.eventsList &&
    ({
      reportedCases: x.eventsList.reduce((acc, y) => acc + y.caseCounts.reportedCases, 0),
      deaths: x.eventsList.reduce((acc, y) => acc + y.caseCounts.deaths, 0),
    } as dto.CaseCountModel)
);
const getFirstActivityUpdateDateStr = createSelector(diseaseActivityList, x => x?.diseaseInformation?.firstActivityUpdateDate);
const getAllEventLocationsFlattened = createSelector(
  diseaseActivityList,
  isLoading,
  (list, loading) => (loading ? null : (list?.eventsList || []).reduce((a, b) => [...a, ...b.eventLocations], [] as dto.EventLocationModel[])) // NOTE: flatten
);
const getSubtitle = createSelector(eventTitle, diseaseActivityPanelsSelectors.disacParams, (eventTitle, disacParams) => {
  const { isVisibleDELP } = mapDiseaseActivityParamsToPanelState(disacParams);
  return isVisibleDELP ? eventTitle : '';
});
const isSelectedEventInvalid = createSelector(diseaseActivityPanelsSelectors.disacParams, diseaseActivityList, (disacParams, allEvents) =>
  disacParams.countryId ? !allEvents.eventsList.find(x => x.eventInformation.diseaseActivityKey.countryId === disacParams.countryId) : false
);

// diseaseActivityListSelectors
export default {
  isLoading,
  isLoadingWrtPreviousPanel,
  hasError,
  diseaseActivityList,
  eventTitle,
  diseaseActivitiesByCountryId,
  importationRisk,
  exportationRisk,
  outbreakPotentialCategory,
  diseaseInformation,
  selectedRiskType,
  selectedRisk,
  getFilteredEventList,
  getDiseaseActivityByCountrySortBy,
  getDiseaseActivityByCountrySearchString,
  isSelectedEventInvalid,
  eventListSortOptions,
  getTotalCaseCounts,
  getFirstActivityUpdateDateStr,
  getAllEventLocationsFlattened,
  getSubtitle,
};
